import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { blogApi } from '../../api/blog';
import { queryKeys } from '../queryClient';
import type { BlogInput } from '../../types/domain';

export function useBlogs() {
  return useQuery({ queryKey: queryKeys.blog, queryFn: () => blogApi.list() });
}

export function useBlogPost(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.blogPost(id ?? ''),
    queryFn: () => blogApi.get(id!),
    enabled: !!id,
  });
}

export function useBlogCategories() {
  return useQuery({ queryKey: queryKeys.blogCategories, queryFn: () => blogApi.categories(), staleTime: 5 * 60_000 });
}

export function useCreateBlog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BlogInput) => blogApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.blog }),
  });
}

export function useUpdateBlog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: BlogInput }) => blogApi.update(id, body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.blog });
      qc.invalidateQueries({ queryKey: queryKeys.blogPost(vars.id) });
    },
  });
}

export function useDeleteBlog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => blogApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.blog }),
  });
}
